import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Edit, Trash2, Eye, EyeOff, FileText, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const AdminLibrary = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [filterAgent, setFilterAgent] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [postsRes, agentsRes] = await Promise.all([
      supabase.from("posts").select("id, title, slug, status, published_at, created_at, agent_id, generation_time_ms").order("created_at", { ascending: false }),
      supabase.from("agents").select("id, name"),
    ]);
    setPosts((postsRes.data as any[]) || []);
    setAgents(agentsRes.data || []);
    setLoading(false);
  };

  const toggleStatus = async (post: any) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    const updates: Record<string, unknown> = { status: newStatus };
    if (newStatus === "published") updates.published_at = new Date().toISOString();
    const { error } = await supabase.from("posts").update(updates).eq("id", post.id);
    if (error) toast({ title: "Erro ao alterar status", variant: "destructive" });
    else {
      toast({ title: newStatus === "published" ? "Post publicado!" : "Post despublicado" });
      fetchData();
    }
  };

  const deletePost = async (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir este post permanentemente?")) return;
    const { error } = await supabase.from("posts").delete().eq("id", id);
    if (error) toast({ title: "Erro ao excluir", variant: "destructive" });
    else {
      toast({ title: "Post excluído" });
      fetchData();
    }
  };

  const agentName = (agentId: string | null) => agents.find((a) => a.id === agentId)?.name || "Manual";

  const filtered = posts
    .filter((p) => filterAgent === "all" || p.agent_id === filterAgent)
    .filter((p) => filterStatus === "all" || p.status === filterStatus)
    .filter((p) => !search || p.title?.toLowerCase().includes(search.toLowerCase()));

  const publishedCount = posts.filter((p) => p.status === "published").length;
  const draftCount = posts.filter((p) => p.status === "draft").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Biblioteca</h1>
        <p className="text-sm text-muted-foreground">
          {posts.length} posts · {publishedCount} publicados · {draftCount} rascunhos
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por título..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={filterAgent} onValueChange={setFilterAgent}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Agente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os agentes</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="published">Publicados</SelectItem>
            <SelectItem value="draft">Rascunhos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center">
          <FileText className="mx-auto mb-3 h-12 w-12 text-muted-foreground/30" />
          <p className="text-muted-foreground">Nenhum post encontrado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((post) => (
            <div key={post.id} className="group flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 transition-all hover:shadow-sm hover:border-primary/20 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-medium text-foreground text-sm sm:text-base">{post.title}</p>
                  <Badge variant={post.status === "published" ? "default" : "secondary"} className={`shrink-0 ${post.status === "published" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0" : ""}`}>
                    {post.status === "published" ? "Publicado" : "Rascunho"}
                  </Badge>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{agentName(post.agent_id)}</span>
                  <span>·</span>
                  <span>{new Date(post.published_at || post.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  {post.generation_time_ms && (
                    <>
                      <span>·</span>
                      <span>{(post.generation_time_ms / 1000).toFixed(1)}s</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleStatus(post)} title={post.status === "published" ? "Despublicar" : "Publicar"}>
                  {post.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Link to={`/admin/posts/${post.id}/edit`}>
                  <Button variant="ghost" size="icon" className="h-8 w-8"><Edit className="h-4 w-4" /></Button>
                </Link>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deletePost(post.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLibrary;
